import { html, TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";

import LengthValidator from "@/internals/validators/length-validator";
import type { Constructor } from "@/internals/util-types";

import type { CharacterCounterTypeChangeEvent } from "@/components/display/atlas-character-counter/types";
import type FormElement from "@/components/form/form-element";

import "@/components/display/atlas-character-counter/atlas-character-counter";

export type WithCharsCounterProps = {
    "maxlength": number;
    "minlength": number;
    "enable-chars-counter": boolean;
};

export declare class WithCharsCounterInterface {
    maxlength: number;

    minlength: number;

    enableCharsCounter: boolean;

    getCurrentCharsCounterValue(): number;

    hasCharsCounter(): boolean;

    hasDefaultCharsCounter(): boolean;

    renderCharsCounter(): TemplateResult;
}

export type FormElementWithCharsCounter = FormElement & WithCharsCounterInterface;

export const WithCharsCounterMixin = <T extends Constructor<FormElement>>(superClass: T) => {
    class WithCharsCounterClass extends superClass implements WithCharsCounterInterface {
        /** Quantidade máxima de caracteres */
        @property({ type: Number, reflect: true }) maxlength: number;

        /** Quantidade mínima de caracteres */
        @property({ type: Number, reflect: true }) minlength: number;

        /** Indica que o contador de caracteres não deve ser exibido */
        @property({ type: Boolean, reflect: true, attribute: "enable-chars-counter" }) enableCharsCounter = false;

        @state() private _hasDefaultCharsCounter = false;

        public constructor(...args: any[]) {
            super(...args);

            this.onCharsCounterTypeChange = this.onCharsCounterTypeChange.bind(this);
        }

        /** @internal */
        public connectedCallback() {
            super.connectedCallback?.();

            this.addLengthValidator();

            this.addEventListener("atlas-character-counter-type-change", this.onCharsCounterTypeChange);
        }

        /** @internal */
        public disconnectedCallback() {
            super.disconnectedCallback?.();

            this.removeEventListener("atlas-character-counter-type-change", this.onCharsCounterTypeChange);
        }

        /** @internal */
        public getCurrentCharsCounterValue() {
            return this.value?.length ?? 0;
        }

        /** @internal */
        public hasCharsCounter() {
            if (!this.enableCharsCounter || this.disabled || this.readonly) return false;

            if (this.isErrorStatusVisible() && !this._hasDefaultCharsCounter) return false;

            return !!this.maxlength || !!this.minlength;
        }

        /** @internal */
        public hasDefaultCharsCounter() {
            return this.hasCharsCounter() && this._hasDefaultCharsCounter;
        }

        private addLengthValidator() {
            const lengthValidator = new LengthValidator();

            if (this.getValidator(lengthValidator.name)) return;

            this.addValidator(lengthValidator);
        }

        private onCharsCounterTypeChange(event: CharacterCounterTypeChangeEvent) {
            this._hasDefaultCharsCounter = event.detail.type === "default";
        }

        private getCharsCounterId() {
            return this.id ? `${this.id}-chars-counter` : "chars-counter";
        }

        /** @internal */
        public renderCharsCounter() {
            return when(
                this.hasCharsCounter(),
                () => html`
                    <atlas-character-counter
                        aria-live="polite"
                        min=${this.minlength}
                        max=${this.maxlength}
                        id=${this.getCharsCounterId()}
                        ?skeleton-loading=${this.skeletonLoading}
                        current=${this.getCurrentCharsCounterValue()}
                        ?highlight-over-max=${this.isErrorStatusVisible()}
                    ></atlas-character-counter>
                `
            );
        }
    }

    return WithCharsCounterClass as Constructor<WithCharsCounterInterface> & T;
};
